import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import DataTable from "@/components/DataTable";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  IdCard, Pencil, FileDown, FileUp, Printer, History, CheckCircle2,
  AlertTriangle, XCircle, Undo2, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import {
  downloadSeatNumberTemplate, exportSeatNumbersList, parseSeatNumberExcel,
} from "@/lib/excel";
import { downloadPaginatedReportPdf } from "@/lib/pdf";

/**
 * إدارة أرقام الجلوس — تعديل فردي أو جماعي (عبر Excel) لأرقام جلوس
 * الطلاب، مستقلة تمامًا عن باقي بيانات الطالب. المطابقة في الاستبدال
 * الجماعي تعتمد حصريًا على معرّف الطالب الداخلي (وليس الاسم أو الترتيب)،
 * وكل تعديل — فردي أو جماعي — لا يغيّر إلا حقل seat_number فقط.
 */
export default function SeatNumbersManage() {
  const { ctx, meta } = useApp();
  const grades = meta?.grades_by_stage?.[ctx.stage] || [];

  const [gradeFilter, setGradeFilter] = useState("");
  const [classFilter, setClassFilter] = useState("__all__");
  const [classes, setClasses] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);

  // Single edit
  const [editOpen, setEditOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [newSeat, setNewSeat] = useState("");
  const [saving, setSaving] = useState(false);

  // Bulk replace
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkStep, setBulkStep] = useState(1); // 1: export/upload, 2: review
  const [parsedRows, setParsedRows] = useState([]);
  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const fileInputRef = useRef(null);

  // Operations history
  const [opsOpen, setOpsOpen] = useState(false);
  const [ops, setOps] = useState([]);
  const [opsLoading, setOpsLoading] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!gradeFilter) { setClasses([]); setClassFilter("__all__"); return; }
    api.get("/students/classes", { params: { stage: ctx.stage, year: ctx.year, grade: gradeFilter } })
      .then(({ data }) => setClasses(data)).catch(() => {});
  }, [ctx.stage, ctx.year, gradeFilter]);

  const load = () => {
    if (!gradeFilter) { setRows([]); return; }
    setLoading(true);
    api.get("/students", {
      params: {
        stage: ctx.stage, year: ctx.year, grade: gradeFilter,
        ...(classFilter !== "__all__" ? { class_name: classFilter } : {}),
      },
    })
      .then(({ data }) => {
        const sorted = [...data].sort((a, b) =>
          (parseInt(a.seat_number, 10) || 0) - (parseInt(b.seat_number, 10) || 0))
          .map((s, i) => ({ ...s, _idx: i + 1 }));
        setRows(sorted);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, [ctx.stage, ctx.year, gradeFilter, classFilter]);

  // ── Single edit ──────────────────────────────────────────────
  const openEdit = (row) => {
    setEditingRow(row);
    setNewSeat(row.seat_number || "");
    setEditOpen(true);
  };
  const saveSingleEdit = async () => {
    const trimmed = (newSeat || "").trim();
    if (!trimmed) return toast.error("رقم الجلوس لا يمكن أن يكون فارغًا");
    if (!/^\d+$/.test(trimmed)) return toast.error("رقم الجلوس يجب أن يكون رقمًا صحيحًا");
    setSaving(true);
    try {
      await api.put(`/students/${editingRow.id}/seat-number`, { seat_number: trimmed });
      toast.success("تم تحديث رقم الجلوس");
      setEditOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  // ── Bulk replace ─────────────────────────────────────────────
  const openBulk = () => {
    setBulkStep(1);
    setParsedRows([]);
    setValidation(null);
    setApplyResult(null);
    setBulkOpen(true);
  };
  const downloadTemplate = () => {
    if (!rows.length) return toast.error("لا يوجد طلاب في التحديد الحالي لتصديرهم");
    downloadSeatNumberTemplate(rows, `نموذج-أرقام-الجلوس-${gradeFilter}.xlsx`);
  };
  const pickFile = () => fileInputRef.current?.click();
  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setValidating(true);
    try {
      const parsed = await parseSeatNumberExcel(file);
      if (!parsed.length) {
        toast.error("لم يتم العثور على صفوف صالحة — تأكد من استخدام نموذج التصدير نفسه بدون تعديل أعمدته");
        setValidating(false);
        return;
      }
      setParsedRows(parsed);
      const { data } = await api.post("/seat-numbers/validate-bulk", {
        stage: ctx.stage, year: ctx.year, rows: parsed,
      });
      setValidation(data);
      setBulkStep(2);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setValidating(false); }
  };
  const applyBulk = async () => {
    if (!validation?.can_apply) return;
    setApplying(true);
    try {
      const { data } = await api.post("/seat-numbers/apply-bulk", {
        stage: ctx.stage, year: ctx.year, rows: parsedRows,
      });
      setApplyResult(data);
      toast.success(`تم تحديث ${data.updated_count} رقم جلوس بنجاح`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setApplying(false); }
  };

  // ── Export / Print ───────────────────────────────────────────
  const exportExcel = () => {
    if (!rows.length) return toast.error("لا يوجد طلاب في التحديد الحالي");
    exportSeatNumbersList(rows, `كشف-أرقام-الجلوس-${gradeFilter}.xlsx`);
  };
  const printSheet = async () => {
    if (!rows.length) return toast.error("لا يوجد طلاب في التحديد الحالي");
    const container = document.createElement("div");
    container.setAttribute("dir", "rtl");
    container.style.position = "fixed";
    container.style.left = "-99999px";
    container.style.top = "0";
    container.style.background = "#ffffff";
    document.body.appendChild(container);
    const wrap = document.createElement("div");
    wrap.style.width = "794px";
    wrap.style.padding = "24px";
    wrap.style.background = "#ffffff";
    container.appendChild(wrap);
    const { createRoot } = await import("react-dom/client");
    const root = createRoot(wrap);
    await new Promise((resolve) => {
      root.render(
        <SeatNumbersSheet school={settings} stage={ctx.stage} grade={gradeFilter}
                           cls={classFilter} students={rows} />
      );
      document.fonts.ready.then(() => setTimeout(resolve, 150));
    });
    try {
      await downloadPaginatedReportPdf(wrap, `كشف-أرقام-الجلوس-${gradeFilter}.pdf`);
      toast.success("تم توليد كشف أرقام الجلوس بنجاح");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { container.remove(); }
  };

  // ── Operations history ───────────────────────────────────────
  const openOps = () => {
    setOpsOpen(true);
    setOpsLoading(true);
    api.get("/seat-numbers/operations").then(({ data }) => setOps(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setOpsLoading(false));
  };
  const undoOp = async (op) => {
    if (!window.confirm(`التراجع عن عملية ${new Date(op.created_at).toLocaleString("ar-EG")} وإرجاع ${op.updated_count} رقم جلوس لقيمها السابقة؟`)) return;
    try {
      const { data } = await api.post(`/seat-numbers/operations/${op.operation_id}/undo`);
      toast.success(`تم التراجع — أُرجِع ${data.restored} رقم جلوس`);
      openOps();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const columns = [
    { key: "m", header: "م", render: (r) => r._idx },
    { key: "name", header: "اسم الطالب", render: (r) => <span className="font-medium text-foreground">{r.name}</span> },
    { key: "grade", header: "الصف" },
    { key: "class", header: "الفصل", render: (r) => r.class || "—" },
    { key: "seat_number", header: "رقم الجلوس الحالي", align: "end",
      render: (r) => <span className="font-semibold tabular-nums">{r.seat_number || "—"}</span> },
  ];

  return (
    <div className="space-y-6" data-testid="seat-numbers-manage-page">
      <PageHeader
        icon={IdCard}
        overline="الشؤون الأكاديمية"
        title="إدارة أرقام الجلوس"
        subtitle="تحديث أرقام الجلوس المؤقتة إلى الأرقام الرسمية — فرديًا أو جماعيًا عبر Excel — دون أي تأثير على درجات أو بيانات الطالب."
        badge={`${rows.length} طالب`}
        actions={
          <>
            <Button variant="outline" onClick={openOps}
                     className="h-10 bg-white/10 border-white/20 text-white hover:bg-white/15"
                     data-testid="sn-ops-btn">
              <History className="h-4 w-4 me-1.5" /> سجل العمليات
            </Button>
            <Button variant="outline" onClick={openBulk}
                     className="h-10 bg-white/10 border-white/20 text-white hover:bg-white/15"
                     data-testid="sn-bulk-btn">
              <FileUp className="h-4 w-4 me-1.5" /> استبدال جماعي
            </Button>
          </>
        }
      />

      <div className="rounded-xl border border-border bg-white p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="space-y-1.5">
          <Label className="text-[12px]">الصف</Label>
          <Select value={gradeFilter} onValueChange={setGradeFilter}>
            <SelectTrigger className="h-10" data-testid="sn-grade-filter"><SelectValue placeholder="اختر الصف" /></SelectTrigger>
            <SelectContent>
              {grades.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-[12px]">الفصل</Label>
          <Select value={classFilter} onValueChange={setClassFilter} disabled={!gradeFilter}>
            <SelectTrigger className="h-10" data-testid="sn-class-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">كل الفصول</SelectItem>
              {classes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end gap-2">
          <Button variant="outline" onClick={exportExcel} disabled={!rows.length} className="h-10 flex-1" data-testid="sn-export-excel">
            <FileDown className="h-4 w-4 me-1.5" /> تصدير Excel
          </Button>
        </div>
        <div className="flex items-end gap-2">
          <Button variant="outline" onClick={printSheet} disabled={!rows.length} className="h-10 flex-1" data-testid="sn-print">
            <Printer className="h-4 w-4 me-1.5" /> طباعة الكشف
          </Button>
        </div>
      </div>

      {!gradeFilter ? (
        <div className="rounded-xl border border-dashed border-border bg-white p-16 text-center text-muted-foreground">
          اختر الصف لعرض الطلاب وإدارة أرقام جلوسهم
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          searchKeys={["name", "seat_number", "class"]}
          actions={(row) => (
            <Button size="icon" variant="ghost" onClick={() => openEdit(row)} data-testid={`sn-edit-${row.id}`}>
              <Pencil className="h-4 w-4" />
            </Button>
          )}
          emptyText={loading ? "جارٍ التحميل…" : "لا يوجد طلاب في هذا التحديد"}
          testId="sn-table"
        />
      )}

      {/* Single-edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              <Pencil className="inline h-5 w-5 me-1.5" /> تعديل رقم الجلوس
            </DialogTitle>
            <DialogDescription>هذا التعديل يغيّر رقم الجلوس فقط، ولا يؤثر على أي بيانات أو درجات أخرى للطالب.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-[12px]">اسم الطالب</Label>
              <div className="mt-1 font-semibold">{editingRow?.name}</div>
            </div>
            <div>
              <Label className="text-[12px]">رقم الجلوس الحالي</Label>
              <div className="mt-1 tabular-nums text-muted-foreground">{editingRow?.seat_number || "—"}</div>
            </div>
            <div className="space-y-1.5">
              <Label>رقم الجلوس الجديد</Label>
              <Input value={newSeat} onChange={(e) => setNewSeat(e.target.value)}
                     inputMode="numeric" dir="ltr" data-testid="sn-edit-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>إلغاء</Button>
            <Button onClick={saveSingleEdit} disabled={saving} data-testid="sn-edit-save">
              {saving ? "جارٍ الحفظ…" : "حفظ التعديل"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk replace dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-3xl" dir="rtl">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              <FileUp className="inline h-5 w-5 me-1.5" /> استبدال أرقام الجلوس جماعيًا
            </DialogTitle>
            <DialogDescription>
              {bulkStep === 1
                ? "صدّر نموذج Excel للطلاب المعروضين حاليًا، عبِّئ عمود «رقم الجلوس الجديد» فقط، ثم ارفعه هنا."
                : "راجع نتيجة الفحص قبل الاعتماد — لن يتم أي تعديل على قاعدة البيانات إلا بعد ضغط «اعتماد واستبدال»."}
            </DialogDescription>
          </DialogHeader>

          {bulkStep === 1 && (
            <div className="space-y-4 py-2">
              <div className="rounded-lg border border-border bg-slate-50 p-4 text-[13px] text-slate-700 space-y-2">
                <div className="font-semibold">الخطوات:</div>
                <ol className="list-decimal list-inside space-y-1">
                  <li>اضغط «تصدير نموذج Excel» — سيحتوي على كل الطلاب المعروضين حاليًا حسب فلتر الصف/الفصل.</li>
                  <li>عبِّئ عمود «رقم الجلوس الجديد» فقط لكل طالب تريد تحديثه — اترك الباقي فارغًا لتخطيه.</li>
                  <li>احفظ الملف كما هو، ثم ارفعه هنا بنفس التنسيق دون حذف أي عمود.</li>
                </ol>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={downloadTemplate} className="flex-1 h-11" data-testid="sn-download-template">
                  <FileDown className="h-4 w-4 me-1.5" /> تصدير نموذج Excel
                </Button>
                <Button onClick={pickFile} disabled={validating} className="flex-1 h-11" data-testid="sn-upload-file">
                  <FileUp className="h-4 w-4 me-1.5" /> {validating ? "جارٍ الفحص…" : "رفع ملف Excel"}
                </Button>
                <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={onFilePicked} data-testid="sn-file-input" />
              </div>
            </div>
          )}

          {bulkStep === 2 && validation && !applyResult && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-3 gap-3">
                <SummaryCard icon={CheckCircle2} color="emerald" label="جاهز للتحديث" value={validation.valid_count} testId="sn-valid-count" />
                <SummaryCard icon={AlertTriangle} color="amber" label="تحذيرات (سيُتخطى)" value={validation.warning_count} testId="sn-warning-count" />
                <SummaryCard icon={XCircle} color="red" label="أخطاء تمنع التنفيذ" value={validation.error_count} testId="sn-error-count" />
              </div>

              <div className="rounded-lg border border-border overflow-hidden">
                <div className="px-3 py-2 bg-slate-50 text-[12px] font-medium text-muted-foreground border-b border-border">
                  تفاصيل كل الصفوف ({validation.total})
                </div>
                <div className="max-h-72 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-white">
                      <tr className="border-b border-border">
                        <th className="px-3 py-2 text-start font-semibold text-muted-foreground">الطالب</th>
                        <th className="px-3 py-2 text-end font-semibold text-muted-foreground">الحالي</th>
                        <th className="px-3 py-2 text-end font-semibold text-muted-foreground">الجديد</th>
                        <th className="px-3 py-2 text-start font-semibold text-muted-foreground">الحالة</th>
                      </tr>
                    </thead>
                    <tbody>
                      {validation.results.map((r, i) => (
                        <tr key={i} className="border-b border-slate-100 last:border-0">
                          <td className="px-3 py-1.5">{r.name || "—"}</td>
                          <td className="px-3 py-1.5 text-end tabular-nums">{r.current_seat_number || "—"}</td>
                          <td className="px-3 py-1.5 text-end tabular-nums font-semibold">{r.new_seat_number || "—"}</td>
                          <td className="px-3 py-1.5">
                            {r.status === "valid" && <span className="text-emerald-700">✅ صالح</span>}
                            {r.status === "warning" && <span className="text-amber-700">⚠️ {r.reason}</span>}
                            {r.status === "error" && <span className="text-red-700">❌ {r.reason}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {!validation.can_apply && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-[13px] text-red-700 flex items-center gap-2">
                  <XCircle className="h-4 w-4 shrink-0" />
                  لا يمكن الاعتماد الآن — يوجد أخطاء يجب إصلاحها في الملف أولًا (أو لا يوجد أي صف صالح للتحديث).
                </div>
              )}
            </div>
          )}

          {applyResult && (
            <div className="space-y-3 py-2">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-3">
                <ShieldCheck className="h-8 w-8 text-emerald-600 shrink-0" />
                <div>
                  <div className="font-semibold text-emerald-800">تم تحديث أرقام الجلوس بنجاح</div>
                  <div className="text-[13px] text-emerald-700 mt-0.5">
                    تم تحديث {applyResult.updated_count} طالب، وتخطي {applyResult.skipped_count}.
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            {bulkStep === 2 && !applyResult && (
              <Button variant="outline" onClick={() => setBulkStep(1)}>رجوع</Button>
            )}
            <Button variant="outline" onClick={() => setBulkOpen(false)}>{applyResult ? "إغلاق" : "إلغاء"}</Button>
            {bulkStep === 2 && !applyResult && (
              <Button onClick={applyBulk} disabled={!validation?.can_apply || applying} data-testid="sn-apply-bulk">
                {applying ? "جارٍ التنفيذ…" : "اعتماد واستبدال أرقام الجلوس"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Operations history dialog */}
      <Dialog open={opsOpen} onOpenChange={setOpsOpen}>
        <DialogContent className="max-w-2xl" dir="rtl">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              <History className="inline h-5 w-5 me-1.5" /> سجل عمليات الاستبدال الجماعي
            </DialogTitle>
            <DialogDescription>يمكن التراجع عن أي عملية سابقة لإرجاع أرقام الجلوس لقيمها قبلها.</DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto">
            {opsLoading ? (
              <div className="text-center py-10 text-muted-foreground text-sm">جارٍ التحميل…</div>
            ) : ops.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground text-sm">لا توجد عمليات سابقة</div>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border">
                    <th className="px-3 py-2 text-start font-semibold text-muted-foreground">التاريخ</th>
                    <th className="px-3 py-2 text-end font-semibold text-muted-foreground">محدَّث</th>
                    <th className="px-3 py-2 text-end font-semibold text-muted-foreground">متخطى</th>
                    <th className="px-3 py-2 text-start font-semibold text-muted-foreground">الحالة</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {ops.map((op) => (
                    <tr key={op.id} className="border-b border-slate-100 last:border-0">
                      <td className="px-3 py-2">{new Date(op.created_at).toLocaleString("ar-EG")}</td>
                      <td className="px-3 py-2 text-end tabular-nums">{op.updated_count}</td>
                      <td className="px-3 py-2 text-end tabular-nums">{op.skipped_count}</td>
                      <td className="px-3 py-2">
                        {op.status === "success" && <span className="text-emerald-700">تم بنجاح</span>}
                        {op.status === "failed" && <span className="text-red-700">فشلت (تم التراجع تلقائيًا)</span>}
                        {op.status === "undone" && <span className="text-slate-500">تم التراجع عنها</span>}
                      </td>
                      <td className="px-3 py-2 text-end">
                        {op.status === "success" && (
                          <Button size="sm" variant="outline" onClick={() => undoOp(op)} data-testid={`sn-undo-${op.id}`}>
                            <Undo2 className="h-3.5 w-3.5 me-1" /> تراجع
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setOpsOpen(false)}>إغلاق</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({ icon: Icon, color, label, value, testId }) {
  const colors = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    red: "border-red-200 bg-red-50 text-red-700",
  }[color];
  return (
    <div className={`rounded-lg border p-4 ${colors}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-4 w-4" />
        <span className="text-[12px] font-medium">{label}</span>
      </div>
      <div className="font-display text-3xl font-semibold tabular-nums" data-testid={testId}>{value}</div>
    </div>
  );
}

/** جدول كشف أرقام الجلوس للطباعة — نفس بنية <thead>/<tbody> التي تعتمد
 * عليها downloadPaginatedReportPdf لتكرار الترويسة وتقسيم الصفحات بعدد
 * صفوف ثابت. */
function SeatNumbersSheet({ school, stage, grade, cls, students }) {
  return (
    <div className="mx-auto max-w-4xl bg-white border-4 border-primary/70 rounded-xl p-8 shadow-sm" dir="rtl">
      <div className="flex items-center justify-between border-b border-slate-200 pb-4">
        <div>
          <div className="font-display text-lg font-bold">{school?.school_name || "المدرسة"}</div>
          <div className="text-sm text-muted-foreground mt-0.5">كشف أرقام الجلوس</div>
        </div>
        {school?.school_logo && (
          <img src={school.school_logo} alt="شعار" style={{ maxHeight: "56px", objectFit: "contain" }} crossOrigin="anonymous" />
        )}
      </div>
      <div className="mt-2 text-sm">
        {stage} — الصف: {grade} — الفصل: {cls === "__all__" ? "كل الفصول" : cls}
      </div>

      <table className="w-full mt-4 border-collapse" style={{ tableLayout: "fixed" }}>
        <thead>
          <tr className="bg-primary text-white" style={{ height: 34 }}>
            <th className="px-2 text-center border border-primary" style={{ width: "8%" }}>م</th>
            <th className="px-2 text-start border border-primary" style={{ width: "42%" }}>اسم الطالب</th>
            <th className="px-2 text-center border border-primary" style={{ width: "18%" }}>الصف</th>
            <th className="px-2 text-center border border-primary" style={{ width: "12%" }}>الفصل</th>
            <th className="px-2 text-center border border-primary" style={{ width: "20%" }}>رقم الجلوس</th>
          </tr>
        </thead>
        <tbody>
          {students.map((s, i) => (
            <tr key={s.id} style={{ height: 30 }}>
              <td className="px-2 text-center border border-slate-200">{i + 1}</td>
              <td className="px-2 border border-slate-200">{s.name}</td>
              <td className="px-2 text-center border border-slate-200">{s.grade}</td>
              <td className="px-2 text-center border border-slate-200">{s.class || "—"}</td>
              <td className="px-2 text-center border border-slate-200 font-bold tabular-nums">{s.seat_number || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
